import React, { createContext, useContext, useMemo, useState } from "react";

export const SUPPORTED_LANGUAGES = ["de", "en"];
export const DEFAULT_LANGUAGE = "de";
const STORAGE_KEY = "ls_language";

const translations = {
  de: {
    languageLabel: "Sprache",
    nearYou: "in deiner Nähe",
    feed: "Feed",
    admin: "Admin",
    adminLogin: "Admin Login",
    today: "Heute",
    tomorrow: "Morgen",
    weekdaysLong: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
    weekdaysShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    locationRequiresHttps: "Standort benötigt HTTPS. Öffne die Seite über eine sichere Verbindung und versuche es erneut.",
    geolocationUnsupported: "Standortbestimmung wird von diesem Browser nicht unterstützt.",
    failedToLoadSpecials: "Angebote konnten nicht geladen werden",
    locationDetected: "Standort erkannt",
    locationUnavailable: "Standort nicht verfügbar. Bitte Standortzugriff erlauben und erneut versuchen.",
    locationUnavailableWithReason: (reason) => `Standort nicht verfügbar: ${reason}`,
    updateLocation: "Standort aktualisieren",
    useMyLocation: "Meinen Standort nutzen",
    clearLocation: "Standort zurücksetzen",
    yourLocation: "Dein Standort",
    heroTitleStart: "Find what's",
    heroTitleHighlight: "cooking",
    heroTitleEnd: "nearby, right now.",
    filter: "Filter",
    collapse: "Einklappen",
    filterSummary: "Suche · Radius · Preis",
    activeFilters: "Aktive Filter",
    resetFilters: "Filter zurücksetzen",
    allCategories: "Alle Kategorien",
    allCuisines: "Alle Küchen",
    expandAll: "Alle aufklappen",
    collapseAll: "Alle zuklappen",
    moreDishes: (count) => `+${count} weitere`,
    search: "Suche",
    searchPlaceholder: "Pasta, Pho, vegan…",
    radius: "Radius",
    maxPrice: "Max. Preis",
    apply: "Anwenden",
    todaysLunch: "Heutiger Mittagstisch",
    dayLunch: (day) => `Mittagstisch am ${day}`,
    restaurants: (count) => `${count} ${count === 1 ? "Restaurant" : "Restaurants"}`,
    dishes: (count) => `${count} ${count === 1 ? "Gericht" : "Gerichte"}`,
    list: "Liste",
    map: "Karte",
    details: "Details anzeigen",
    mapNoRestaurants: "Keine Restaurants mit Kartenkoordinaten für diese Auswahl.",
    loading: "Lädt…",
    noLunchForDay: (day) => `Keine Mittagsangebote für ${day}`,
    emptyHint: "Versuche einen anderen Tag oder erweitere den Radius.",
    website: "Webseite",
    navigateToRestaurant: (name) => `Zu ${name || "Restaurant"} navigieren`,
    category: {
      starter: "Vorspeise",
      main: "Hauptgericht",
      soup: "Suppe",
      salad: "Salat",
      dessert: "Dessert",
      drink: "Getränk",
      combo: "Menü",
      other: "Sonstiges",
      lunch: "Mittag",
    },
    dayCodes: { Daily: "Täglich", Mon: "Mo", Tue: "Di", Wed: "Mi", Thu: "Do", Fri: "Fr", Sat: "Sa", Sun: "So" },
  },
  en: {
    languageLabel: "Language",
    nearYou: "near you",
    feed: "Feed",
    admin: "Admin",
    adminLogin: "Admin login",
    today: "Today",
    tomorrow: "Tomorrow",
    weekdaysLong: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    weekdaysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    locationRequiresHttps: "Location requires HTTPS. Open this site over a secure connection and try again.",
    geolocationUnsupported: "Geolocation is not supported by this browser.",
    failedToLoadSpecials: "Failed to load specials",
    locationDetected: "Location detected",
    locationUnavailable: "Location unavailable. Please allow location access and try again.",
    locationUnavailableWithReason: (reason) => `Location unavailable: ${reason}`,
    updateLocation: "Update location",
    useMyLocation: "Use my location",
    clearLocation: "Clear location",
    yourLocation: "Your location",
    heroTitleStart: "Find what's",
    heroTitleHighlight: "cooking",
    heroTitleEnd: "nearby, right now.",
    filter: "Filter",
    collapse: "Collapse",
    filterSummary: "Search · Radius · Price",
    activeFilters: "Active filters",
    resetFilters: "Reset filters",
    allCategories: "All categories",
    allCuisines: "All cuisines",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    moreDishes: (count) => `+${count} more`,
    search: "Search",
    searchPlaceholder: "Pasta, pho, vegan…",
    radius: "Radius",
    maxPrice: "Max price",
    apply: "Apply",
    todaysLunch: "Today's lunch",
    dayLunch: (day) => `${day}'s lunch`,
    restaurants: (count) => `${count} restaurants`,
    dishes: (count) => `${count} dishes`,
    list: "List",
    map: "Map",
    details: "Show details",
    mapNoRestaurants: "No restaurants with map coordinates for this selection.",
    loading: "Loading…",
    noLunchForDay: (day) => `No lunch specials for ${day.toLowerCase()}`,
    emptyHint: "Try a different day or widen your radius.",
    website: "Website",
    navigateToRestaurant: (name) => `Navigate to ${name || "Restaurant"}`,
    category: {
      starter: "starter",
      main: "main",
      soup: "soup",
      salad: "salad",
      dessert: "dessert",
      drink: "drink",
      combo: "combo",
      other: "other",
      lunch: "lunch",
    },
    dayCodes: { Daily: "Daily", Mon: "Mon", Tue: "Tue", Wed: "Wed", Thu: "Thu", Fri: "Fri", Sat: "Sat", Sun: "Sun" },
  },
};

function normalizeLanguage(value) {
  return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
}

function readStoredLanguage() {
  if (typeof window === "undefined" || !window.localStorage) return DEFAULT_LANGUAGE;
  try {
    return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: translations[DEFAULT_LANGUAGE],
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readStoredLanguage);
  const setLanguage = (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    try {
      window.localStorage?.setItem(STORAGE_KEY, normalized);
    } catch {
      // best-effort persistence only
    }
  };
  const value = useMemo(() => ({ language, setLanguage, t: translations[language] }), [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function getLocalizedText(item, field, language) {
  const translationsByLanguage = item?.[`${field}_translations`] || {};
  const localized = translationsByLanguage?.[language];
  return localized || item?.[field] || "";
}

export function localizeSpecial(special, language) {
  return {
    ...special,
    display_name: getLocalizedText(special, "name", language),
    display_description: getLocalizedText(special, "description", language),
  };
}

export function translateDayCodes(days = [], t = translations[DEFAULT_LANGUAGE]) {
  return days.map((day) => t.dayCodes?.[day] || day);
}

export function translateCategory(category, t = translations[DEFAULT_LANGUAGE]) {
  if (!category) return null;
  return t.category?.[category] || category;
}
